
import React, { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Mic, Square, AlertCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import AudioWaveform from './AudioWaveform';

interface VoiceRecorderProps {
  onTranscriptionComplete: (text: string) => void;
  apiKey: string;
  placeholder?: string;
}

const VoiceRecorder: React.FC<VoiceRecorderProps> = ({ 
  onTranscriptionComplete, 
  apiKey,
  placeholder = "Click the microphone to start recording..." 
}) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const recordingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const startRecording = async () => {
    // Reset audio chunks at the beginning of recording
    audioChunksRef.current = [];

    try {
      // Request microphone access with quality settings for voice
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          // Use simpler audio settings to ensure compatibility
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          // Explicitly set a reasonable sample rate
          sampleRate: 44100,
          channelCount: 1
        } 
      });
      
      streamRef.current = stream;
      
      // Create audio context if it doesn't exist
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)({
          // Match the sample rate to our audio constraints
          sampleRate: 44100
        });
      }

      // Ensure the audio context is running
      if (audioContextRef.current.state === 'suspended') {
        await audioContextRef.current.resume();
      }
      
      // Create and configure MediaRecorder with simplified, compatible settings
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus'
      });
      
      mediaRecorderRef.current = mediaRecorder;
      
      // Set up data collection
      mediaRecorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          audioChunksRef.current.push(event.data);
          console.log(`Added audio chunk: ${event.data.size} bytes, type: ${event.data.type}`);
        }
      };

      // Set up stop handler
      mediaRecorder.onstop = async () => {
        console.log(`Recording stopped with ${audioChunksRef.current.length} chunks`);
        
        // Check if we have any audio data before trying to transcribe
        if (audioChunksRef.current.length === 0) {
          console.error("No audio chunks collected during recording");
          toast.error("No audio was recorded. Please try again.");
          setIsTranscribing(false);
          return;
        }
        
        // Create audio blob from chunks
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        
        // Check if blob is empty
        if (audioBlob.size === 0) {
          console.error("Audio blob is empty (0 bytes)");
          toast.error("No audio data was captured. Please try again.");
          setIsTranscribing(false);
          return;
        }
        
        if (audioBlob.size < 1000) {
          console.warn(`Audio blob is very small (${audioBlob.size} bytes), may not contain enough valid audio data`);
        }
        
        console.log(`Audio blob created: ${audioBlob.size} bytes, type: ${audioBlob.type}`);
        
        // Proceed with transcription
        await transcribeAudio(audioBlob);
        
        // Release microphone and clean up resources
        cleanupAudioResources();
      };

      // Create analyser node for visualization
      analyserRef.current = audioContextRef.current.createAnalyser();
      analyserRef.current.fftSize = 2048;
      
      // Connect microphone to analyser (but not to audio destination to avoid feedback)
      const source = audioContextRef.current.createMediaStreamSource(stream);
      source.connect(analyserRef.current);
      
      // Start recording with 250ms chunks for more frequent updates
      mediaRecorder.start(250);
      
      // Set UI state to recording
      setIsRecording(true);
      toast.success("Recording started");
      
      // Add a safety timeout to stop recording after 2 minutes
      // to prevent runaway recordings
      recordingTimeoutRef.current = setTimeout(() => {
        if (isRecording) {
          stopRecording();
          toast.info("Recording automatically stopped after 2 minutes");
        }
      }, 120000); // 2 minutes
      
    } catch (err) {
      console.error("Error accessing microphone:", err);
      toast.error("Could not access microphone. Please check your browser permissions.");
    }
  };

  const stopRecording = () => {
    // Clear the safety timeout
    if (recordingTimeoutRef.current) {
      clearTimeout(recordingTimeoutRef.current);
      recordingTimeoutRef.current = null;
    }
    
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      // First update UI state to ensure waveform stops animating
      setIsRecording(false);
      
      // Request a final chunk of audio data
      if (mediaRecorderRef.current.state === 'recording') {
        mediaRecorderRef.current.requestData();
      }
      
      // Add a small delay to ensure the requestData has time to process
      setTimeout(() => {
        // Then stop the recorder (which will trigger the onstop handler)
        try {
          if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
            mediaRecorderRef.current.stop();
            setIsTranscribing(true);
            toast.success("Recording stopped");
          }
        } catch (err) {
          console.error("Error stopping recording:", err);
          toast.error("Error stopping recording");
          setIsTranscribing(false);
          cleanupAudioResources();
        }
      }, 100);
    } else {
      setIsRecording(false);
      toast.warning("No active recording to stop");
    }
  };

  const cleanupAudioResources = () => {
    // Stop and release microphone stream
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    
    // Reset MediaRecorder
    mediaRecorderRef.current = null;
    
    // Don't close AudioContext immediately as it may be needed for future recordings
    // Just disconnect analyser if it exists
    if (analyserRef.current) {
      analyserRef.current.disconnect();
    }
  };

  const transcribeAudio = async (audioBlob: Blob) => {
    if (!apiKey) {
      toast.error("Deepgram API key is not set");
      setIsTranscribing(false);
      return;
    }

    if (!audioBlob || audioBlob.size === 0) {
      toast.error("No audio recorded");
      setIsTranscribing(false);
      return;
    }

    setIsTranscribing(true);

    try {
      // Create a debug audio element to verify the blob is valid (won't be shown to user)
      const debugAudio = new Audio(URL.createObjectURL(audioBlob));
      debugAudio.onloadedmetadata = () => {
        console.log(`Debug: Audio duration is ${debugAudio.duration} seconds`);
      };
      debugAudio.onerror = (e) => {
        console.error("Debug: Audio blob failed to load", e);
      };

      // Create FormData with the audio file
      const formData = new FormData();
      formData.append('audio', audioBlob, 'recording.webm');

      console.log("Sending audio for transcription, size:", audioBlob.size);
      
      // Call Deepgram API
      const response = await fetch('https://api.deepgram.com/v1/listen?model=nova-2&smart_format=true', {
        method: 'POST',
        headers: {
          'Authorization': `Token ${apiKey}`,
        },
        body: formData
      });

      const responseText = await response.text();
      console.log("Deepgram raw response:", responseText);
      
      if (!response.ok) {
        throw new Error(`Deepgram API error: ${responseText}`);
      }

      // Parse the response
      const data = JSON.parse(responseText);
      const transcript = data.results?.channels[0]?.alternatives[0]?.transcript || '';
      
      if (transcript) {
        onTranscriptionComplete(transcript);
        toast.success("Transcription completed");
      } else {
        toast.warning("No speech detected");
      }
    } catch (error) {
      console.error("Transcription error:", error);
      toast.error(`Error transcribing audio: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsTranscribing(false);
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Stop any active recording
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
      
      // Clear any timeouts
      if (recordingTimeoutRef.current) {
        clearTimeout(recordingTimeoutRef.current);
      }
      
      // Clean up resources
      cleanupAudioResources();
      
      // Close audio context on unmount
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        audioContextRef.current.close().catch(console.error);
        audioContextRef.current = null;
      }
    };
  }, []);

  return (
    <div className="flex flex-col space-y-3">
      <div className="flex items-center">
        {!apiKey && (
          <div className="flex items-center text-amber-600 text-sm mb-2">
            <AlertCircle className="h-4 w-4 mr-2" />
            <span>Deepgram API key not set. Voice recording is disabled.</span>
          </div>
        )}
      </div>
      
      <div className="flex items-center space-x-3">
        {!isRecording ? (
          <Button 
            type="button" 
            variant="outline" 
            size="icon"
            onClick={startRecording}
            disabled={isTranscribing || !apiKey}
            className="h-10 w-10 rounded-full"
          >
            <Mic className="h-5 w-5 text-primary" />
          </Button>
        ) : (
          <Button 
            type="button" 
            variant="destructive" 
            size="icon"
            onClick={stopRecording}
            className="h-10 w-10 rounded-full animate-pulse"
          >
            <Square className="h-5 w-5" />
          </Button>
        )}
        
        <div className="flex-1">
          {isTranscribing ? (
            <span className="text-sm text-muted-foreground flex items-center">
              <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Transcribing...
            </span>
          ) : (
            <span className="text-sm text-muted-foreground">
              {isRecording ? "Recording..." : placeholder}
            </span>
          )}
        </div>
      </div>
      
      {/* Audio Waveform Component */}
      <AudioWaveform 
        isRecording={isRecording} 
        audioContext={audioContextRef.current} 
        analyser={analyserRef.current} 
      />
    </div>
  );
};

export default VoiceRecorder;
